import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { Service } from 'webapp-types';
import { MetricOptionType, SelectSourceOptionType, SourceTypeCategory } from 'webapp/enums/enums';
import { sortBy, sortByPopularity, uniqBy } from 'webapp/utils/common';

import type Metric from 'webapp/models-custom/metric';
import Serviced from 'webapp/models-custom/serviced';
import type SourceRecord from 'webapp/models/source';
import type TemplateRecord from 'webapp/models/template';

import type { ConnectSourceOptions } from 'webapp/services/connect';
import type { SourceTypeExtraInfo } from 'webapp/services/model-manager/source-types';

export enum SourceTypeStatus {
	STABLE = 'stable',
	BETA = 'beta',
	SOON = 'soon',
}

const SourceTypeKeyCustom = 'Custom';

// we want to show 15 minutes sync label only for these sources
const Show15MinutesSyncLabelKeys = [
	'GoogleAnalytics4',
	'FbAds',
	'GoogleAdwords',
	'MySQL',
	'PostgreSQL',
	'MSSQL',
	'AzureSQL',
	'BigQuery',
	'AmazonRedshift',
	'Snowflake',
	'GoogleSheets',
	'ExcelOneDrive',
	'ExcelGoogleDrive',
	'ExcelDropbox',
	'CustomIntegration',
];

const SqlIntegrationKeys = [
	'MySQL',
	'PostgreSQL',
	'MSSQL',
	'AzureSQL',
	'BigQuery',
	'AmazonRedshift',
	'Snowflake',
];

interface ArticleUrlData {
	type?: string;
	title: string;
	url: string;
}

interface CustomMetricsData {
	name: string;
	description: string;
}
interface GroupInfoData {
	text1: string;
	text2: string;
}

export const enum HorizonStatus {
	BETA = 'beta',
	STABLE = 'stable',
}

export interface SourceTypeData {
	id: number | null;
	key: string;
	created: number;
	name: string;
	description: string;
	avatar: string;
	avatarBig: string;
	categories: string[];
	metricBuilderIntegration?: boolean;
	timezone?: string | null;
	status: SourceTypeStatus;
	isNative?: boolean;
	isQBSupported?: boolean;
	isPremium?: boolean;
	isDb?: boolean;
	isSheet?: boolean;
	isHidden?: boolean;
	isFileUpload?: boolean;
	historicalDataPersistenceSupported?: boolean;
	enabledForSpaceIds?: number[];
	customMetrics?: CustomMetricsData[];
	articleUrls?: ArticleUrlData[];
	connectNotes?: string;
	connectNotesLink?: string;
	popup?: boolean;
	popupWidth?: number;
	popupHeight?: number;
	groupInfo?: GroupInfoData; // used only for groups (like Excel) - currently used for group-source popup to show title and description
	groups?: string[]; // used for integrations to define in which group belongs to (like ExcelDrive in Excel group)
	showInGroupOnly?: boolean;
	connectRedirectUrl?: string;
	isFirstStepBasicAuth?: boolean;
	availableSourceOptions?: SelectSourceOptionType[];
	availableMetricOptions?: MetricOptionType[];
	horizonStatus?: HorizonStatus | null;
	datasetHistoryLimitSupported?: boolean;
}

export interface SourceTypePopularMetric {
	key: string;
	longKey: string;
	sourceTypeKey: string;
	sourceTypeId: number | null;
	usedCount: number;
}

export default class SourceType extends Serviced {
	@service('connect') declare connectService: Service.Connect;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked animationClass = ''; // Used for animations in lazy-loading

	id: number | null = null;
	key = '';
	created = 0;
	name = '';
	description = '';
	avatar = '';
	avatarBig = '';
	categories: string[];
	metricBuilderIntegration = false;
	timezone: string | null = '';
	status = '';
	isNative;
	isQBSupported;
	isPremium;
	isDb;
	isSheet;
	isHidden;
	isFileUpload;
	isFirstStepBasicAuth;
	historicalDataPersistenceSupported;
	enabledForSpaceIds: number[];
	customMetrics: CustomMetricsData[] = [];
	articleUrls: ArticleUrlData[] = [];
	connectNotes = '';
	connectNotesLink = '';
	popup;
	popupWidth = 0;
	popupHeight = 0;
	groupInfo: GroupInfoData | null = null; // used only for groups (like Excel) - currently used for group-source popup to show title and description
	groups: string[] = []; // used for integrations to define in which group belongs to (like ExcelDrive in Excel group)
	showInGroupOnly; // mostly used for filtering - if true show only main group item
	connectRedirectUrl = ''; // special connect flow - redirect to some url (vendor marketplace)

	popularity = 9999; // set by returned index (mmSourceTypes.requestSourceTypes)
	suggestionScore = 9999; // set by onboarding experiment

	availableSourceOptions: SelectSourceOptionType[] = [];
	availableMetricOptions: MetricOptionType[] = [];
	horizonStatus: HorizonStatus | null = null;
	datasetHistoryLimitSupported;

	constructor(sourceTypeData: SourceTypeData) {
		super();

		if (typeof sourceTypeData.id === 'number') {
			this.id = sourceTypeData.id;
		}

		this.key = sourceTypeData.key;
		this.created = sourceTypeData.created;
		this.name = sourceTypeData.name;
		this.description = sourceTypeData.description;
		this.avatar = sourceTypeData.avatar;
		this.avatarBig = sourceTypeData.avatarBig;
		this.categories = sourceTypeData.categories || [];
		this.metricBuilderIntegration = !!sourceTypeData.metricBuilderIntegration;
		this.timezone =
			typeof sourceTypeData.timezone !== 'undefined' ? sourceTypeData.timezone : '';
		this.status = sourceTypeData.status;
		this.isNative = !!sourceTypeData.isNative;
		this.isQBSupported = !!sourceTypeData.isQBSupported;
		this.isPremium = !!sourceTypeData.isPremium;
		this.isDb = !!sourceTypeData.isDb;
		this.isSheet = !!sourceTypeData.isSheet;
		this.isHidden = !!sourceTypeData.isHidden;
		this.isFileUpload = !!sourceTypeData.isFileUpload;
		this.historicalDataPersistenceSupported =
			!!sourceTypeData.historicalDataPersistenceSupported;
		this.enabledForSpaceIds = sourceTypeData.enabledForSpaceIds || [];
		this.popup = !!sourceTypeData.popup;
		this.isFirstStepBasicAuth = !!sourceTypeData.isFirstStepBasicAuth;
		this.datasetHistoryLimitSupported = !!sourceTypeData.datasetHistoryLimitSupported;

		if (sourceTypeData.customMetrics) {
			this.customMetrics = sourceTypeData.customMetrics;
		}
		if (sourceTypeData.articleUrls) {
			this.articleUrls = sourceTypeData.articleUrls;
		}
		if (sourceTypeData.connectNotes) {
			this.connectNotes = sourceTypeData.connectNotes;
		}
		if (sourceTypeData.connectNotesLink) {
			this.connectNotesLink = sourceTypeData.connectNotesLink;
		}
		if (sourceTypeData.popupWidth) {
			this.popupWidth = sourceTypeData.popupWidth;
		}
		if (sourceTypeData.popupHeight) {
			this.popupHeight = sourceTypeData.popupHeight;
		}
		if (sourceTypeData.groupInfo) {
			this.groupInfo = sourceTypeData.groupInfo;
		}
		if (sourceTypeData.groups) {
			this.groups = sourceTypeData.groups;
		}
		if (sourceTypeData.connectRedirectUrl) {
			this.connectRedirectUrl = sourceTypeData.connectRedirectUrl;
		}
		this.showInGroupOnly = !!sourceTypeData.showInGroupOnly;

		if (sourceTypeData.availableSourceOptions) {
			this.availableSourceOptions = sourceTypeData.availableSourceOptions;
		}

		if (sourceTypeData.availableMetricOptions) {
			this.availableMetricOptions = sourceTypeData.availableMetricOptions;
		}

		this.horizonStatus = sourceTypeData.horizonStatus ?? null;
	}

	get isStable(): boolean {
		return this.status === SourceTypeStatus.STABLE;
	}

	get isBeta(): boolean {
		return this.status === SourceTypeStatus.BETA;
	}

	get isSoon(): boolean {
		return this.status === SourceTypeStatus.SOON;
	}

	@cached
	get isNew(): boolean {
		return moment().unix() <= moment.unix(this.created).add(3, 'month').unix();
	}

	get isPopular(): boolean {
		return this.popularity < 9;
	}

	@cached
	get isRecommended(): boolean {
		return this.accountService.metadata?.sourcesSelection?.includes(this.key);
	}

	get isRecommendedNotConnected(): boolean {
		return this.isRecommended && !this.isAlreadyConnected;
	}

	get isToken(): boolean {
		return this.key === SourceTypeKeyCustom;
	}

	get isZapier(): boolean {
		return this.key === 'Zapier';
	}

	get isMake(): boolean {
		return this.key === 'Make';
	}

	get isSalesforce(): boolean {
		return this.key === 'Salesforce';
	}

	get isYouTube(): boolean {
		return this.key === 'YouTube';
	}

	get isGoogleSheets(): boolean {
		return this.key === 'GoogleSheets';
	}

	get isDatasetSupported(): boolean {
		return (
			this.availableSourceOptions.includes(SelectSourceOptionType.DATASETS) ||
			this.availableSourceOptions.includes(SelectSourceOptionType.DATASET_CUSTOM_INTEGRATION)
		);
	}

	get isDatasetOnly(): boolean {
		return (
			this.availableSourceOptions.length > 0 &&
			this.isDatasetSupported &&
			!this.availableSourceOptions.includes(SelectSourceOptionType.NATIVE)
		);
	}

	get isExcelGroup(): boolean {
		return this.key === 'Excel';
	}

	get isExcel(): boolean {
		return this.isExcelGroup || this.groups?.includes('Excel');
	}

	get isSheets(): boolean {
		return this.isSheet;
	}

	get isCustomRESTApi(): boolean {
		return this.key === 'CustomIntegration';
	}

	get isDatasetCustomIntegration(): boolean {
		return this.key === 'CustomIntegrations';
	}

	get isTokenWrapper(): boolean {
		return this.id === 0;
	}

	get isWithCustomToken(): boolean {
		return this.isToken || this.isTokenWrapper;
	}

	get isWorkflowIntegration() {
		return this.isZapier || this.isMake;
	}

	get showConnectHelp() {
		return !this.isWithCustomToken && !this.isFileUpload; // do not support that on connect help (yet?)
	}

	get show15MinSyncLabelPromo() {
		return Show15MinutesSyncLabelKeys.includes(this.key);
	}

	get isGroup(): boolean {
		return !!this.groupInfo;
	}

	get groupItems(): SourceType[] {
		const groupItems: SourceType[] = [];

		this.mmSourceTypesService.sourceTypeModels.forEach((sourceType) => {
			if (sourceType.groups?.includes(this.key)) {
				groupItems.push(sourceType);
			}
		});

		return groupItems;
	}

	get isConnectable(): boolean {
		return !!this.id || this.isWithCustomToken;
	}

	get isAlreadyConnected(): boolean {
		return this.connectedSources.length > 0;
	}

	get isQbOnly(): boolean {
		return this.isQBSupported && this.isDb && !this.hasBasicMetrics && !this.hasPublicTemplates;
	}

	get isHorizonSupported(): boolean {
		return this.availableMetricOptions.includes(MetricOptionType.HORIZON);
	}

	get isHorizonBeta(): boolean {
		return this.horizonStatus === HorizonStatus.BETA;
	}

	get isSqlConnector(): boolean {
		// uses isDb behind the scenes
		return (this.connectService?.sqlConnectors || []).includes(this.key);
	}

	get isSqlishConnector(): boolean {
		return this.isSqlConnector || this.isSalesforce;
	}

	get isSQLType(): boolean {
		return SqlIntegrationKeys.includes(this.key);
	}

	get hasPublicTemplates(): boolean {
		return this.publicTemplates.length > 0;
	}

	get isCustomMetricWizardPossible(): boolean {
		return this.isSheets || this.metricBuilderIntegration;
	}

	get isLegacyCustomSourceType(): boolean {
		return this.isSQLType || this.isSheets || this.isToken;
	}

	get legacyCustomSourceTypeSourceMigrationLink(): string {
		if (this.isSQLType) {
			return 'https://help.databox.com/migrate-from-legacy-sql-metrics-to-datasets';
		} else if (this.isSheets) {
			return 'https://help.databox.com/migrate-from-legacy-spreadsheet-metrics-to-datasets';
		} else if (this.isToken) {
			return 'https://help.databox.com/migrate-from-legacy-api-v0-to-databox-api-v1';
		}
		return '';
	}

	@cached
	get connectedSources(): SourceRecord[] {
		return this.mmSourcesService.savedRecords.filter((source: SourceRecord) => {
			return this.isSourceFromThisType(source);
		});
	}

	@cached
	get publicTemplates(): TemplateRecord[] {
		const id = this.id;
		if (!id) {
			return [];
		}
		return this.mmTemplatesService.publicTemplateRecords.filter((template) => {
			return template.sources.includes(id);
		});
	}

	@cached
	get metricsByPopularity(): { key: string; usedCount: number; name: string }[] {
		const metrics = this.mmMetricsService
			.getGenericMetricsForSourceTypeKey(this.key)
			.map((m) => {
				return {
					key: m.key,
					usedCount: m.usedCount,
					name: m.name,
				};
			});

		return metrics.sort((a, b) => b.usedCount - a.usedCount);
	}

	@cached
	get metricsByPopularityNames() {
		return this.metricsByPopularity.map((m) => m.name).join(' ');
	}

	@cached
	get connectedBasicMetrics() {
		const sources = this.connectedSources,
			metrics: Metric[] = [];

		sources.forEach((source) => {
			const metricsForSource = this.mmMetricsService
				.getMetricsForSpaceAccessId(+source.id!)
				.filter((m) => {
					return m.isTypeBasic;
				});
			metrics.push(...sortByPopularity(metricsForSource));
		});

		return metrics;
	}

	get uniqueArticleUrls(): SourceTypeData['articleUrls'] {
		return uniqBy(this.articleUrls, 'url');
	}

	get genericGuideLink(): string {
		const article = this.articleUrls.find((item) => item.type === 'connector');
		return article?.url || '';
	}

	get connectLink(): string {
		const article = this.articleUrls.find((item) => item.type === 'connect');
		return article?.url || '';
	}

	get qbLink(): string {
		const article = this.articleUrls.find((item) => item.type === 'querybuilder');
		return article?.url || '';
	}

	get connectLinkWithFallback(): string {
		return this.connectLink || this.genericGuideLink;
	}

	get qbLinkWithFallback(): string {
		return (
			this.qbLink ||
			this.genericGuideLink ||
			'https://help.databox.com/create-a-custom-metric'
		);
	}

	get connectNotesFormatted(): string {
		let connectNotesFormatted = '',
			text = this.connectNotes || '',
			extraClass = '';

		if (this.connectNotesLink) {
			text += ` <a href="${this.connectNotesLink}" target="_blank">Learn more</a>`;
		}

		if (this.key === 'GoogleAnalytics') {
			extraClass = 'connect-notes--alert';
		}

		if (text) {
			connectNotesFormatted = `<div class="connect-notes ${extraClass}"><ui-icon name="AlertFill" size="sm"></ui-icon><span>${text}</span></div>`;
		}

		return connectNotesFormatted;
	}

	get category(): SourceTypeCategory {
		let category;

		if (this.isToken) {
			category = SourceTypeCategory.REST_API;
		} else if (this.isTokenWrapper) {
			category = SourceTypeCategory.WORKFLOW;
		} else if (this.isSheets) {
			category = SourceTypeCategory.SPREADSHEET;
		} else if (this.metricBuilderIntegration) {
			category = SourceTypeCategory.METRIC_BUILDER;
		} else if (this.isDb) {
			category = SourceTypeCategory.DATABASE;
		} else if (this.isNative) {
			category = SourceTypeCategory.CLOUD;
		} else {
			category = SourceTypeCategory.PARTNER;
		}

		return category;
	}

	get shownBadges(): string[] {
		const badges = [];

		if (this.isDatasetSupported) {
			badges.push('datasets');
		}

		if (this.isBeta) {
			badges.push('beta');
		} else if (this.isNew) {
			badges.push('new');
		}

		return badges;
	}

	get qbSupportText(): string {
		let text = '';
		if (this.isQBSupported) {
			text = '<strong>Support for built metrics</strong>';
			text += '<p>This data source integration supports the creation of built metrics.</p>';

			if (!this.accountService.hideHelpArticles) {
				const articleUrl = this.qbLink;
				if (articleUrl) {
					text += '<a href="' + articleUrl + '" target="_blank">Learn more</a>';
				}
			}
		}
		return text;
	}

	get latestToken(): string | null {
		let token;
		if (this.isWithCustomToken) {
			token = sortBy(this.connectedSources, 'created').slice(-1)[0]?.token;
		}
		return token || null;
	}

	get extraInfo(): SourceTypeExtraInfo {
		return this.mmSourceTypesService.sourceTypesExtraInfoByKey[this.key];
	}

	get categoryArray(): string[] {
		return this.categories.map((category) => category.split(',').map((c) => c.trim())).flat();
	}

	@cached
	get basicMetricRecords() {
		return this.mmMetricsService.getBasicMetricRecordsForSourceTypeKey(this.key);
	}

	get hasBasicMetrics(): boolean {
		return this.basicMetricRecords.length > 0;
	}

	@cached
	get predefinedMetricRecords() {
		return this.mmMetricsService.getGenericPredefinedMetricRecordsForSourceTypeKey(this.key);
	}

	get hasBasicOrPredefinedMetrics() {
		return this.basicMetricRecords.length > 0 || this.predefinedMetricRecords.length > 0;
	}

	isSourceFromThisType(source: SourceRecord) {
		let isFromThisType = false;

		if (this.id) {
			isFromThisType = source.type === this.id;
		} else {
			// customs
			if (source.wrapperFor) {
				isFromThisType = source.wrapperFor === this.key;
			} else {
				isFromThisType = source.type === 0 && this.isToken;
			}
		}
		return isFromThisType;
	}

	connectNew(options?: ConnectSourceOptions) {
		return this.connectService.connectSource(this, options);
	}

	requestConnect(isOnboarding = false): void {
		this.mmSourceTypesService.requestConnect(this, isOnboarding);
	}

	requestExtraInfo(): void {
		this.mmSourceTypesService.requestExtraInfoByKey(this.key);
	}
}
